import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { PerformanceTimingService } from './services/performance-timing.service';
import { AuthService } from './services/auth.service';
import { CheckoutReturnService } from './services/checkout-return.service';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'PropDesk';
  showCookieBanner = false;

  private readonly consentStorageKey = 'propdesk_cookie_consent';

  constructor(
    private performanceTiming: PerformanceTimingService,
    private auth: AuthService,
    public checkoutReturn: CheckoutReturnService,
    router: Router,
    titleService: Title
  ) {
    // A page's own title (SeoService / route `title`) is set after this, so pages that set none
    // fall back to the site title instead of keeping whichever page was visited before.
    const defaultTitle = titleService.getTitle();
    // Only on a real page change: query-only navigations (tab switches) don't re-create the page, so
    // nothing would set its title again.
    let lastPath = '';
    router.events.subscribe(e => {
      if (!(e instanceof NavigationStart)) return;
      const path = e.url.split(/[?#]/)[0];
      if (path === lastPath) return;
      lastPath = path;
      // Admin-only areas set no title of their own; label each as a section rather than the site default.
      const area = /^\/(admin|insights)(\/|$)/.exec(path)?.[1];
      titleService.setTitle(area === 'admin' ? 'Admin | PropDesk' : area === 'insights' ? 'Insights | PropDesk' : defaultTitle);
    });
  }

  ngOnInit(): void {
    this.performanceTiming.start();
    this.auth.startAfterInitialRender();
    const savedConsent = localStorage.getItem(this.consentStorageKey);

    if (savedConsent === 'accepted') {
      this.updateGoogleConsent('granted');
      return;
    }

    if (savedConsent === 'rejected') {
      this.updateGoogleConsent('denied');
      return;
    }

    this.showCookieBanner = true;
  }

  acceptCookies(): void {
    localStorage.setItem(this.consentStorageKey, 'accepted');
    this.updateGoogleConsent('granted');
    this.showCookieBanner = false;
  }

  rejectCookies(): void {
    localStorage.setItem(this.consentStorageKey, 'rejected');
    this.updateGoogleConsent('denied');
    this.showCookieBanner = false;
  }

  private updateGoogleConsent(value: 'granted' | 'denied'): void {
    window.gtag?.('consent', 'update', {
      analytics_storage: value,
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value
    });
  }
}


